import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { enumsExpirationDays } from "utils/enums";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import LinkHome from "components/atoms/LinkHome";
import Context from "contextAPI/UserContext";

const HeaderTitlePage = (props) => {
  const { t } = useTranslation();
  const { heading, icon, subheading } = props;
  const enablePageTitleIcon = useSelector((state) => state.ThemeOptions.enablePageTitleIcon);
  const enablePageTitleSubheading = useSelector(
    (state) => state.ThemeOptions.enablePageTitleSubheading
  );
  const { passwordExpirationDays } = useContext(Context);

  let renderAlertExpirationPassword = null;
  let renderAlertExpirationUltimateDay = null;

  /**
   * Renders an alert when the user's password is about to expire.
   * This block of code checks if the `passwordExpirationDays` is within the range
   * of 1 to 7 days. If true, it renders an alert message with a link to the
   * password renewal page.
   * @constant {number} passwordExpirationDays - The number of days remaining until the password expires.
   * @var {JSX.Element} renderAlertExpirationPassword - The JSX element that displays the alert for password expiration.
   * @returns {JSX.Element|null} The alert component if `passwordExpirationDays` is within the specified range, otherwise null.
   */
  if (
    passwordExpirationDays !== null &&
    passwordExpirationDays <= enumsExpirationDays.SEVEN_DAYS_EXPIRATION &&
    passwordExpirationDays > enumsExpirationDays.ULTIMATE_DAY_EXPIRATION
  ) {
    renderAlertExpirationPassword = (
      <div className="content-alert expiration-days-header">
        <Alert color="info" className="expiration-days-alert">
          <span className="pr-2 text-bold">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 " />
          </span>
          <Link to="/renew-password">
            <p>
              {t("password.expiration.label1")}
              {"\n"}
              {passwordExpirationDays} {t("password.expiration.label2")}
            </p>
          </Link>
        </Alert>
      </div>
    );
  }

  /**
   * Renders an alert when the user's password expires today.
   * This block of code checks if the `passwordExpirationDays` is exactly 0, indicating that
   * the password expires today. If true, it renders an alert message with a link to the
   * password renewal page.
   * @constant {number} passwordExpirationDays - The number of days remaining until the password expires.
   * @var {JSX.Element} renderAlertExpirationUltimateDay - The JSX element that displays the alert for password expiration on the final day.
   * @returns {JSX.Element|null} The alert component if `passwordExpirationDays` is 0, otherwise null.
   */
  if (passwordExpirationDays === enumsExpirationDays.ULTIMATE_DAY_EXPIRATION) {
    renderAlertExpirationUltimateDay = (
      <div className="content-alert expiration-days-header">
        <Alert color="danger" className="expiration-days-alert">
          <span className="pr-2 text-bold">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 " />
          </span>
          <p>
            <Link to="/renew-password" className="expiration-days-link">
              {t("password.expiration.label3")}
            </Link>
          </p>
        </Alert>
      </div>
    );
  }

  return (
    <div className="app-page-title">
      {renderAlertExpirationPassword}
      {renderAlertExpirationUltimateDay}
      <div className="page-title-wrapper">
        <div className="page-title-heading">
          <div
            className={cx("page-title-icon rounded-circle", {
              "d-none": !enablePageTitleIcon,
            })}
          >
            <i className={icon} />
          </div>
          <div>
            {heading}
            <div
              className={cx("page-title-subheading", {
                "d-none": !enablePageTitleSubheading,
              })}
            >
              {subheading}
              <LinkHome />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

HeaderTitlePage.propTypes = {
  heading: PropTypes.string.isRequired,
  icon: PropTypes.node,
  subheading: PropTypes.string,
};

export default HeaderTitlePage;
